/**
 * WindowTabs - Tab navigation for switching between windows
 */

import React, { useState } from 'react';
import { Plus, X, BookOpen, Home, List, Crosshair, BarChart3 } from 'lucide-react';
import { useWindows, JOURNAL_WINDOW_ID, FOCUS_WINDOW_ID, ALL_TASKS_WINDOW_ID, ACTIVITY_WINDOW_ID } from '../../contexts/WindowsContext';

export function WindowTabs() {
  const { config, activeWindow, setActiveWindow, createWindow, deleteWindow, homeWindowId, setDefaultWindow, isEditing } = useWindows();
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const handleCreate = () => {
    if (newName.trim()) {
      createWindow(newName.trim());
      setNewName('');
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreate();
    } else if (e.key === 'Escape') {
      setIsCreating(false);
      setNewName('');
    }
  };

  return (
    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg overflow-x-auto">
      {config.windows.map((window) => (
        <button
          key={window.id}
          onClick={() => setActiveWindow(window.id)}
          className={`group flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeWindow?.id === window.id
              ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          {window.id === homeWindowId && (
            <Home className="w-3 h-3 text-blue-500 dark:text-blue-400 flex-shrink-0" />
          )}
          <span>{window.name}</span>

          {/* Set as home - visible on hover for non-home tabs */}
          {window.id !== homeWindowId && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setDefaultWindow(window.id);
              }}
              className="ml-0.5 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity"
              title="Set as home screen"
            >
              <Home className="w-3 h-3" />
            </span>
          )}

          {/* Close button - visible on hover in edit mode, don't show for last window */}
          {isEditing && config.windows.length > 1 && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Delete window "${window.name}"?`)) {
                  deleteWindow(window.id);
                }
              }}
              className="ml-1 p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity"
            >
              <X className="w-3 h-3" />
            </span>
          )}
        </button>
      ))}

      {/* Journal tab */}
      <button
        onClick={() => setActiveWindow(JOURNAL_WINDOW_ID)}
        className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
          config.activeWindowId === JOURNAL_WINDOW_ID
            ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        <BookOpen className="w-4 h-4" />
        <span>Journal</span>
      </button>

      {/* Focus tab */}
      <button
        onClick={() => setActiveWindow(FOCUS_WINDOW_ID)}
        className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
          config.activeWindowId === FOCUS_WINDOW_ID
            ? 'bg-white dark:bg-gray-700 text-orange-600 dark:text-orange-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        <Crosshair className="w-4 h-4" />
        <span>Focus</span>
      </button>

      {/* All Tasks tab */}
      <button
        onClick={() => setActiveWindow(ALL_TASKS_WINDOW_ID)}
        className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
          config.activeWindowId === ALL_TASKS_WINDOW_ID
            ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        <List className="w-4 h-4" />
        <span>All Tasks</span>
      </button>

      {/* Activity tab */}
      <button
        onClick={() => setActiveWindow(ACTIVITY_WINDOW_ID)}
        className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
          config.activeWindowId === ACTIVITY_WINDOW_ID
            ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        <BarChart3 className="w-4 h-4" />
        <span>Activity</span>
      </button>

      {/* New window input/button */}
      {isCreating ? (
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onBlur={() => {
            if (!newName.trim()) {
              setIsCreating(false);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder="Window name..."
          autoFocus
          className="px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md outline-none focus:ring-2 focus:ring-blue-500"
        />
      ) : (
        <button
          onClick={() => setIsCreating(true)}
          className="flex items-center gap-1 px-2 py-2 rounded-md text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          title="Create new window"
        >
          <Plus className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
